import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import {
  selectors,
  actions,
} from 'builder/modules/conversationalBuilder/conversationalBuilderModule'
import { State } from './types'

export function useConversationalBuilder() {
  const dispatch = useDispatch()

  const messages = useSelector(selectors.messages)
  const loading = useSelector(selectors.loading)
  const error = useSelector(selectors.error)
  const pendingChanges = useSelector(selectors.pendingChanges)

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()

      const form = e.currentTarget
      const formData = new FormData(form)
      const prompt = formData.get('prompt') as string

      dispatch(actions.sendMessage(prompt))

      form.reset()
    },
    [dispatch],
  )

  const handleRefresh = useCallback(() => {
    if (!messages || messages.length === 0) return

    const lastUserMessage = messages
      .slice()
      .reverse()
      .find(m => m.role === 'user')
    if (!lastUserMessage) return

    dispatch(actions.refreshLastMessage())
  }, [dispatch, messages])

  const handleApplyChanges = useCallback(() => {
    dispatch(actions.applyPendingChanges())
  }, [dispatch])

  const sendMessage = useCallback(
    (prompt: string) => {
      dispatch(actions.sendMessage(prompt))
    },
    [dispatch],
  )

  return {
    messages,
    handleSubmit,
    handleRefresh,
    loading,
    error,
    pendingChanges,
    handleApplyChanges,
    sendMessage,
  }
}

export function useChatScroll(props: { open: boolean; messages: State }) {
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const chatBodyRef = useRef<HTMLDivElement | null>(null)

  const scrollToBottom = useCallback(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTo({
        top: chatBodyRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [])

  const trackScroll = useCallback(() => {
    if (chatBodyRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatBodyRef.current
      const isAtBottom = scrollTop + clientHeight > scrollHeight - 100

      setShowScrollIndicator(!isAtBottom)
    }
  }, [])

  useEffect(() => {
    if (props.open && chatBodyRef.current) {
      scrollToBottom()
    }
  }, [props.messages, props.open])

  return { chatBodyRef, showScrollIndicator, trackScroll, scrollToBottom }
}

export function useConversationalBuilderFeature() {
  const { features } = useFeaturesConfig()
  return { isConversationalBuilderEnabled: features.conversationalBuilder }
}
